import { useEffect, useState } from "react";
import { TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, Divider, Chip, MenuItem, Autocomplete, Typography, FormControlLabel, Checkbox } from "@mui/material";

import _ from "lodash";
import axios from "axios";
import Swal from "sweetalert2";

export default function EditProductForm({ open, handleClose, rowData, fetchData, userCountry }) {
    const [editData, setEditData] = useState({});
    const [millionProducts, setMillionProducts] = useState([]);
    const [productGroups, setProductGroups] = useState([]);
    const [selectedMillionProduct, setSelectedMillionProduct] = useState(null);
    const [millionSearch, setMillionSearch] = useState("");
    const [initialMillionCode, setInitialMillionCode] = useState("");
    const [isMillionSelectionInitialized, setIsMillionSelectionInitialized] = useState(false);

    const [errors, setErrors] = useState({ product_name: false, product_description: false, country_id: false });
    const [helperText, setHelperText] = useState({});
    const [errorMessage, setErrorMessage] = useState("");

    const normalizedSelectedMillionProduct =
        selectedMillionProduct && millionProducts.some((item) => item.code === selectedMillionProduct.code)
            ? millionProducts.find((item) => item.code === selectedMillionProduct.code)
            : null;

    useEffect(() => {
        if (rowData) {
            setEditData({
                ...rowData,
                product_group_id: rowData.product_group_id || null,
                is_contract_product: Number(rowData.is_contract_product) === 1 ? 1 : 0,
            });
            setSelectedMillionProduct(null);
            setInitialMillionCode(rowData.million_product_code || "");
            setIsMillionSelectionInitialized(false);
        }
        if (open) {
            setErrors({ product_name: false, product_description: false, country_id: false });
            setHelperText({});
            setErrorMessage("");
            setMillionSearch(rowData?.million_product_code || "");
        }
    }, [rowData, open]);

    useEffect(() => {
        if (!open) {
            return;
        }

        const fetchProductGroups = async () => {
            if (!editData.country_id) {
                setProductGroups([]);
                return;
            }

            try {
                const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/product-groups/select", {
                    params: {
                        country: editData.country_id,
                    },
                });

                const groups = response.data?.data?.productGroups || response.data?.data?.groups || [];
                setProductGroups(groups);

                setEditData((prev) => {
                    if (prev.product_group_id === null || prev.product_group_id === "") {
                        return prev;
                    }

                    const hasSelectedGroup = groups.some((group) => Number(group.product_group_id) === Number(prev.product_group_id));
                    if (hasSelectedGroup) {
                        return prev;
                    }

                    return { ...prev, product_group_id: null };
                });
            } catch (error) {
                setProductGroups([]);
                console.log(error);
            }
        };

        fetchProductGroups();
    }, [open, editData.country_id]);

    useEffect(() => {
        if (!open) {
            return;
        }

        const fetchMillionProducts = async () => {
            if (!editData.country_id) {
                setMillionProducts([]);
                setSelectedMillionProduct(null);
                return;
            }

            try {
                const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/million-products", {
                    params: {
                        page: 1,
                        perPage: 200,
                        country_id: editData.country_id,
                        status: "all",
                        search: millionSearch,
                    },
                });

                const products = response.data.data.millionProducts || [];

                // Keep one option per canonical Million code to avoid duplicate keys in autocomplete.
                const uniqueProducts = Object.values(
                    products.reduce((acc, item) => {
                        if (!item?.code) {
                            return acc;
                        }
                        if (!acc[item.code]) {
                            acc[item.code] = item;
                        }
                        return acc;
                    }, {})
                );

                setMillionProducts(uniqueProducts);

                setSelectedMillionProduct((prev) => {
                    if (!prev?.code) {
                        return prev;
                    }

                    const matchedSelected = uniqueProducts.find((item) => item.code === prev.code);
                    return matchedSelected || null;
                });

                if (!isMillionSelectionInitialized && initialMillionCode) {
                    const matchedProduct = uniqueProducts.find((item) => item.code === initialMillionCode);
                    if (matchedProduct) {
                        setSelectedMillionProduct(matchedProduct);
                    } else {
                        setSelectedMillionProduct(null);
                    }
                    setIsMillionSelectionInitialized(true);
                }
            } catch (error) {
                console.log(error);
            }
        };

        fetchMillionProducts();
    }, [open, millionSearch, initialMillionCode, isMillionSelectionInitialized, editData.country_id]);

    if (!rowData) return null;

    const setError = async (field, message) => {
        await setErrors((prevErrors) => ({ ...prevErrors, [field]: true }));
        await setHelperText((prevHelperText) => ({ ...prevHelperText, [field]: message }));
    };

    const validate = async (field, value) => {
        if (field === "product_name" && value === "") {
            await setError("product_name", "Name is required.");
        } else if (field === "product_description" && value === "") {
            await setError("product_description", "Description is required.");
        } else if (field === "country_id" && value === "") {
            await setError("country_id", "Manage By is required.");
        } else {
            await setErrors((prevErrors) => ({ ...prevErrors, [field]: false }));
            await setHelperText((prevHelperText) => ({ ...prevHelperText, [field]: "" }));
        }
    };

    const handleEditProduct = async () => {
        try {
            const { product_name, product_description, country_id, is_contract_product } = editData;
            const response = await axios.patch(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/products/${rowData.product_id}`, {
                product_name,
                product_description,
                country_id,
                product_group_id: editData.product_group_id === null || editData.product_group_id === "" ? null : Number(editData.product_group_id),
                is_contract_product,
                million_product_code: selectedMillionProduct?.code || null,
                price: selectedMillionProduct?.price ?? null,
            });

            if (response.status === 200) {
                handleClose();
                Swal.fire({
                    title: "Success",
                    text: "Product information updated successfully",
                    icon: "success",
                    showConfirmButton: false,
                    timer: 2000,
                });
                fetchData();
            }
        } catch (error) {
            if (error.response && error.response.data) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage("An error occurred while updating the product information. Please try again later.");
            }
        }
    };

    return (
        <Dialog
            maxWidth="md"
            fullWidth
            open={open}
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">Edit Product Information</DialogTitle>
            {errorMessage && (
                <Alert variant="outlined" severity="error" style={{ marginLeft: "24px", marginRight: "24px" }}>
                    {errorMessage}
                </Alert>
            )}
            <DialogContent style={{ paddingBottom: "0px" }}>
                <Divider style={{ paddingTop: "0px", paddingBottom: "4px" }}>
                    <Chip label="Product Details" />
                </Divider>
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="product_name"
                    label="Name"
                    type="text"
                    fullWidth
                    required
                    defaultValue={rowData.product_name}
                    error={errors.product_name}
                    helperText={helperText.product_name}
                    inputProps={{ style: { textTransform: "uppercase" } }}
                    onChange={(e) => {
                        setEditData({ ...editData, product_name: e.target.value ? e.target.value.toUpperCase() : "", });
                        validate("product_name", e.target.value);
                    }}
                />
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="product_description"
                    label="Description"
                    type="text"
                    multiline
                    rows={3}
                    fullWidth
                    required
                    defaultValue={rowData.product_description}
                    error={errors.product_description}
                    helperText={helperText.product_description}
                    inputProps={{ style: { textTransform: "uppercase" } }}
                    onChange={(e) => {
                        setEditData({ ...editData, product_description: e.target.value ? e.target.value.toUpperCase() : "", });
                        validate("product_description", e.target.value);
                    }}
                />
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="country_id"
                    select
                    label="Manage By"
                    type="number"
                    fullWidth
                    required
                    value={_.findIndex(userCountry, { country_id: editData.country_id }) < 0 ? "" : editData.country_id || ""}
                    error={errors.country_id}
                    helperText={helperText.country_id}
                    onChange={(e) => {
                        setEditData({ ...editData, country_id: e.target.value, product_group_id: null });
                        setSelectedMillionProduct(null);
                        setMillionSearch("");
                        setIsMillionSelectionInitialized(true);
                        validate("country_id", e.target.value);
                    }}
                >
                    {userCountry.map((country) => (
                        <MenuItem key={country.country_id} value={country.country_id}>
                            {country.country_name}
                        </MenuItem>
                    ))}
                </TextField>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={Number(editData.is_contract_product) === 1}
                            onChange={(e) => {
                                setEditData({ ...editData, is_contract_product: e.target.checked ? 1 : 0, product_group_id: e.target.checked ? editData.product_group_id : null });
                            }}
                        />
                    }
                    label="Contract Product"
                    sx={{ mt: 0.5 }}
                />
                {Number(editData.is_contract_product) === 1 && (
                    <TextField
                        autoComplete="off"
                        margin="dense"
                        id="product_group_id"
                        select
                        label="Product Group"
                        type="number"
                        fullWidth
                        value={editData.product_group_id === null ? "" : editData.product_group_id}
                        onChange={(e) => {
                            setEditData({ ...editData, product_group_id: e.target.value === "" ? null : e.target.value });
                        }}
                    >
                        <MenuItem value="">None</MenuItem>
                        {productGroups.map((group) => (
                            <MenuItem key={group.product_group_id} value={group.product_group_id}>
                                {group.group_name}
                            </MenuItem>
                        ))}
                    </TextField>
                )}

                <Divider style={{ paddingTop: "12px", paddingBottom: "4px" }}>
                    <Chip label="Million Mapping (Optional)" />
                </Divider>

                <Autocomplete
                    options={millionProducts}
                    value={normalizedSelectedMillionProduct}
                    onChange={(event, value) => {
                        setIsMillionSelectionInitialized(true);
                        setSelectedMillionProduct(value);
                    }}
                    onInputChange={(event, inputValue, reason) => {
                        if (reason === "input") {
                            setMillionSearch(inputValue);
                        } else if (reason === "clear") {
                            setMillionSearch("");
                        }
                    }}
                    isOptionEqualToValue={(option, value) => option.code === value?.code}
                    getOptionLabel={(option) => `${option?.code || ""}${option?.description ? ` - ${option.description}` : ""}`}
                    renderOption={(props, option) => (
                        <li {...props} key={option.code}>
                            {option.code} - {option.description}
                        </li>
                    )}
                    renderInput={(params) => <TextField {...params} margin="dense" label="Link Million Product" placeholder="Search by code or description" />}
                    fullWidth
                />

                <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                    {selectedMillionProduct
                        ? `Selected mapping: ${selectedMillionProduct.code} | Price: ${selectedMillionProduct.price ?? "-"}`
                        : "No Million product selected."}
                </Typography>
            </DialogContent>
            <DialogActions style={{ margin: "12px" }}>
                <Button onClick={handleEditProduct} variant="contained" color="primary" style={{ marginRight: "6px", color: "white" }}>
                    Update
                </Button>
                <Button onClick={handleClose} variant="contained" color="error" style={{ marginRight: "6px", color: "white" }}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
